import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Landing from "@/pages/Landing";
import Directory from "@/pages/Directory";
import Pledge from "@/pages/Pledge";
import News from "@/pages/News";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import SubmitBusiness from "@/pages/SubmitBusiness";
import AdminDashboard from "@/pages/AdminDashboard";
import Events from "@/pages/Events";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Shop from "@/pages/Shop";
import Founders from "@/pages/Founders";
import FounderDetail from "@/pages/FounderDetail";
import SubmitFounder from "@/pages/SubmitFounder";
import Ambassadors from "@/pages/Ambassadors";
import Spotlight from "@/pages/Spotlight";
import JoinTheLabel from "@/pages/JoinTheLabel";
import CreditUnion from "@/pages/CreditUnion";
import FieldOfMembership from "@/pages/FieldOfMembership";
import Organizers from "@/pages/Organizers";
import MyPledge from "@/pages/MyPledge";
import Rally from "@/pages/Rally";
import TheCase from "@/pages/TheCase";
import TDSPPump from "@/pages/TDSPPump";
import Unsubscribe from "@/pages/Unsubscribe";
import Board from "@/pages/Board";
import Flyers from "@/pages/Flyers";
import MilestoneLetter from "@/pages/MilestoneLetter";
import MemberSurvey from "@/pages/MemberSurvey";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import SupporterTicker from "@/components/SupporterTicker";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-bdu-cream/50">Loading...</div>;
  if (!user || user === false) return <Navigate to={`/login?next=${encodeURIComponent(pathname)}`} replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

// Scroll to #anchor targets (menu links like /credit-union#subscribe), waiting for lazy content.
function ScrollToHash() {
  const { pathname, hash, key } = useLocation();
  useEffect(() => {
    if (!hash) return undefined;
    const id = decodeURIComponent(hash.slice(1));
    let tries = 0;
    const t = setInterval(() => {
      const el = document.getElementById(id);
      if (el || ++tries > 30) {
        clearInterval(t);
        el?.scrollIntoView({ block: "start" });
      }
    }, 100);
    return () => clearInterval(t);
  }, [pathname, hash, key]);
  return null;
}

function AppShell() {
  return (
    <div className="App">
      <AnalyticsTracker />
      <ScrollToHash />
      <SupporterTicker />
      <Nav />
      <main>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/directory" element={<Directory />} />
          <Route path="/pledge" element={<Pledge />} />
          <Route path="/news" element={<News />} />
          <Route path="/events" element={<Events />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/founders" element={<Founders />} />
          <Route path="/founders/submit" element={<ProtectedRoute><SubmitFounder /></ProtectedRoute>} />
          <Route path="/founders/:id" element={<FounderDetail />} />
          <Route path="/ambassadors" element={<Ambassadors />} />
          <Route path="/spotlight" element={<Spotlight />} />
          <Route path="/join-the-label" element={<JoinTheLabel />} />
          <Route path="/credit-union" element={<CreditUnion />} />
          <Route path="/field-of-membership" element={<FieldOfMembership />} />
          <Route path="/fom" element={<Navigate to="/field-of-membership" replace />} />
          <Route path="/organizers" element={<Organizers />} />
          <Route path="/organizing-group" element={<Navigate to="/organizers" replace />} />
          <Route path="/rally" element={<Rally />} />
          <Route path="/the-case" element={<TheCase />} />
          <Route path="/tdsp-pump" element={<TDSPPump />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/board" element={<Board />} />
          <Route path="/flyers" element={<Flyers />} />
          <Route path="/survey" element={<MemberSurvey />} />
          <Route path="/milestone-letter" element={<ProtectedRoute><MilestoneLetter /></ProtectedRoute>} />
          <Route path="/committee" element={<Navigate to="/board" replace />} />
          <Route path="/my-pledge/:code" element={<MyPledge />} />
          <Route path="/fcu" element={<Navigate to="/credit-union" replace />} />
          <Route path="/rise" element={<Navigate to="/join-the-label" replace />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/submit-business" element={<ProtectedRoute><SubmitBusiness /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <Toaster theme="dark" position="top-right" toastOptions={{
        style: { background: "#12100E", border: "1px solid rgba(212,175,55,0.2)", color: "#FDFBF7" }
      }} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
